import React, { useEffect, useRef, useState } from "react";
import { useTraffic } from "store/wheel/selectors";
import styled from "styled-components";
import { Cancel, Share } from '@mui/icons-material';
import { revokeTrafficModal } from "store/wheel/actions";
import { confirmUnshareWidget } from "store/widgets/confirmUnshare";
import { useDispatch } from "react-redux";
import { find, get } from "lodash";
import { v4 } from 'uuid';
import ResizeAndDragabble from "./ResizeAndDraggable";
import { useCurrentUser } from "store/users/selectors";
import { useCollectionWidgets } from "utils/methods/firebaseWidgets";
import AccordinButton from "./AccordinButton";
import AnimatedCheckMark from "./AnimatedCheckMark";
import StoryWithImages from "./StoryWithAi/StoryView";
import { handleListStoryById, handleListStoryAiConfig } from "store/StoryAiCofig/thunks";
import { useOneStoryWithAi } from "store/StoryAiCofig/selectors";
import Loading from "components/Loading";
import { Select } from "antd";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  position: relative;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 0px;
  z-index: 99999999;
  position: fixed;
  cursor: grab;
  transform: translate(31%, 0%);

  @media (max-width: 1450px) {
    transform: translate(0%, 0%);
  }
  @media (max-width: 992px) {
    transform: translate(0%, 0%);
  }
`;

const StoryContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const WidgetsStory = ({ currentClass, sharableWidget }) => {
  const [showAccordin, setShowAccordin] = useState(true);
  const [trafficModal] = useTraffic();
  const currentUser = useCurrentUser();
  const { createCollectionWidgets } = useCollectionWidgets();
  const [dimensions, setDimensions] = useState({});
  const dispatch = useDispatch();
  const isTeacher = get(currentUser, "role") === "teacher";
  const trafficData = trafficModal?.widget_menu ?? [];
  const filterByWidget = find(
    trafficData,
    (item) => item?.menu === "widget_story"
  );
  const filterShareStory = find(sharableWidget, (obj) => obj?.widget_type === 'widget_story');
  const [storyData, setStoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [availableStories, setAvailableStories] = useState([]);
  const imageRef = useRef(null);
  const imageConfigurationRef = useRef(null);
  const hasStoryWithAi = useOneStoryWithAi();

  useEffect(() => {
    if (imageConfigurationRef.current) {
      imageConfigurationRef.current = {
        clientHeight: imageConfigurationRef.current.clientHeight
      };
    }
  }, [dimensions, showAccordin]);

  useEffect(() => {
    if (isTeacher && hasStoryWithAi && Array.isArray(hasStoryWithAi) && hasStoryWithAi.length > 0) {
      setAvailableStories(hasStoryWithAi);
      if (filterShareStory?.data?.story_id && !selectedStoryId) {
        setSelectedStoryId(filterShareStory.data.story_id);
        const foundStory = hasStoryWithAi.find(s => s.id === filterShareStory.data.story_id);
        if (foundStory) {
          setStoryData(foundStory);
        }
      }
    }
  }, [hasStoryWithAi, isTeacher, filterShareStory]);

  useEffect(() => {
    const fetchStoryData = async () => {
      if (filterShareStory?.status && filterShareStory?.data?.story_id) {
        const storyId = filterShareStory.data.story_id;
        if (isTeacher && availableStories.length > 0) {
          const foundStory = availableStories.find(s => s.id === storyId);
          if (foundStory) {
            setStoryData(foundStory);
            setSelectedStoryId(storyId);
            return;
          }
        }
        if (!storyData || storyData.id !== storyId) {
          setLoading(true);
          try {
            const result = await dispatch(handleListStoryById(storyId));
            if (result && result?.id) {
              setStoryData(result);
              if (isTeacher) {
                setSelectedStoryId(storyId);
              }
            }
          } catch (error) {
            console.error("Error fetching story:", error);
          } finally {
            setLoading(false);
          }
        }
      } else if (!filterShareStory?.status) {
        setStoryData(null);
        setSelectedStoryId(null);
      }
    };
    fetchStoryData();
  }, [filterShareStory, dispatch, isTeacher, availableStories]);

  const onClose = async () => {
    const shouldClose = await confirmUnshareWidget({
      isShared: filterShareStory?.status,
      widgetName: "Story widget",
    });
    if (!shouldClose) return;
    const closeShareData = {
      ...filterShareStory,
      status: false,
      message: "story",
    }
    await dispatch(revokeTrafficModal(filterByWidget?.id));
    if (filterShareStory?.status) {
      createCollectionWidgets(closeShareData);
    }
  };

  const handleStorySelect = (storyId) => {
    setSelectedStoryId(storyId);
    const foundStory = availableStories.find(s => s.id === storyId);
    if (foundStory) {
      setStoryData(foundStory);
    } else if (storyId) {
      setLoading(true);
      dispatch(handleListStoryById(storyId)).then((result) => {
        if (result && result?.id) {
          setStoryData(result);
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  };

  const shareStory = () => {
    if (!filterShareStory?.status) {
      if (!selectedStoryId && !storyData?.id) {
        return;
      }
      const shareData = {
        id: !filterShareStory?.id || v4(),
        class_id: currentClass?.id,
        created_by: currentUser?.id,
        created_at: new Date(),
        widget_type: 'widget_story',
        status: true,
        data: {
          story_id: selectedStoryId || storyData?.id || null
        }
      }
      createCollectionWidgets(shareData)
    }
    else {
      const unshareData = {
        ...filterShareStory,
        status: false,
        data: {},
        message: 'widget_story'
      };
      createCollectionWidgets(unshareData)
    }
  }

  const handleTouchClose = (event) => {
    event.preventDefault();
    onClose();
  };

  const handleTouchShare = (event) => {
    event.preventDefault();
    shareStory();
  };

  const sharedData = filterShareStory?.data || {}

  return (
    <>
      {((filterByWidget?.view && filterByWidget?.menu === "widget_story") || filterShareStory?.id) && (
        <Container className="allCheckListSEction wigetsContainer">
          <div className="mainCheckList">
            <ResizeAndDragabble
              defaultValue={{
                x: 150,
                y: 205,
                width: 500,
                height: 600,
              }}
              minWidth={400}
              minHeight={400}
              aspectRatio={false}
              resize={(data) => setDimensions(data)}
              page={"story"}
              showAccordin={showAccordin}
            >
              <div
                className="checkListTopSection addWidgetSpc"
                style={{
                  position: "relative",
                  boxShadow: "rgba(0, 0, 0, 0.2) 0px 2px 4px",
                  height: showAccordin ? "100%" : "0px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  placeItems: "center",
                  padding: "0px"
                }}
              >
                <div
                  className={`checkListFirstTxt widgetFooterGradientOrange w-full bg-blue-10 pt-17px pb-14px ${
                    showAccordin ? "br-topLeft-topRight-20px" : "br-15px"
                  } `}
                >
                  <AccordinButton
                    open={showAccordin}
                    onClose={() => setShowAccordin((prev) => !prev)}
                    shareableWidget={sharableWidget}
                  />

                  <span className="text-white text-ft12-700">Story</span>
                </div>

                {isTeacher && (
                  <Cancel
                    onClick={() => onClose()}
                    onTouchStart={handleTouchClose}
                    style={{
                      marginLeft: "5px",
                      position: "absolute",
                      right: 7,
                      top: 16,
                      fontSize: 18,
                      color: "red",
                      background: "white",
                      borderRadius: 20,
                      cursor: "pointer"
                    }}
                  />
                )}
                {isTeacher && (
                  <div style={{
                    marginBottom: "20px",
                  }}>
                    <span
                      onClick={() => shareStory()}
                      onTouchStart={handleTouchShare}
                      style={{
                        marginLeft: "5px",
                        position: "absolute",
                        right: 34,
                        top: 17,
                        cursor: "pointer"
                      }}>
                      {!filterShareStory?.status ?
                        <Share style={{ color: "white", fontSize: 16 }} />
                        : <AnimatedCheckMark />
                      }
                    </span>
                  </div>
                )}
                {showAccordin && (
                  <StoryContentWrapper ref={imageConfigurationRef}>
                    {isTeacher && !storyData && (
                      <div style={{ 
                        padding: '20px',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Select a Story:</label>
                        <Select
                          placeholder="Choose a story to share"
                          value={selectedStoryId}
                          onChange={handleStorySelect}
                          style={{ width: '100%' }}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={availableStories.map(story => ({
                            value: story.id,
                            label: story.story_title || `Story ${story.id}`
                          }))}
                        />
                        {availableStories.length === 0 && (
                          <div style={{ fontSize: '12px', color: '#ccc', marginTop: '10px' }}>
                            No stories available. Stories will appear here when you create them.
                          </div>
                        )}
                      </div>
                    )}
                    {loading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Loading size={40} white={true} />
                      </div>
                    ) : storyData ? (
                      <StoryWithImages
                        imageConfiguration={imageConfigurationRef.current}
                        ref={imageRef}
                        images={storyData?.images || []}
                        text={storyData?.story_content || ''}
                        title={storyData?.story_title || ''}
                        storyInfo={storyData}
                        is_student={!isTeacher}
                        isStream={false}
                        enable_student_share={false}
                        isLevel={false}
                      />
                    ) : !isTeacher && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%',
                        color: 'white',
                        padding: '20px',
                        textAlign: 'center'
                      }}>
                        No story available.
                      </div>
                    )}
                  </StoryContentWrapper>
                )}

                {showAccordin && (
                  <div className="widgetFooter widgetFooterGradientOrange"><label>Widget</label></div>
                )}
              </div>

            </ResizeAndDragabble>

          </div>
        </Container>
      )}
    </>
  );
};

export default WidgetsStory;

